/**
 * Growthly — Instagram to Shopify V2 (Full-Page Campaign Section)
 * File: assets/instagram-to-shopify-v2.js
 * Libraries: Lenis (smooth scroll) + GSAP 3 + ScrollTrigger
 * Supports Shopify Online Store 2.0 Theme Editor lifecycle & prefers-reduced-motion
 */
(function () {
  'use strict';

  var instances = new WeakMap();
  var globalLenis = null;
  var globalRafId = null;

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initGlobalLenis(reduced) {
    if (reduced || globalLenis || typeof window.Lenis === 'undefined') return;

    globalLenis = new window.Lenis({
      lerp: 0.092,
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 0.95,
    });

    if (window.gsap && window.ScrollTrigger) {
      globalLenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add(function (time) {
        if (globalLenis) globalLenis.raf(time * 1000);
      });
      window.gsap.ticker.lagSmoothing(0);
    } else {
      var loop = function (time) {
        if (globalLenis) {
          globalLenis.raf(time);
          globalRafId = window.requestAnimationFrame(loop);
        }
      };
      globalRafId = window.requestAnimationFrame(loop);
    }
  }

  function initSection(root) {
    if (!root || instances.has(root)) return;

    var reduced = prefersReducedMotion();
    var cleanups = [];
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    initGlobalLenis(reduced);

    function on(el, evt, fn, opts) {
      if (!el) return;
      el.addEventListener(evt, fn, opts || false);
      cleanups.push(function () {
        el.removeEventListener(evt, fn, opts || false);
      });
    }

    // Critically-damped Apple iOS SpringBoard fluid curve (zero cartoon bounce)
    function iosFluidEase(t) {
      return 1 - Math.pow(1 - t, 4.35);
    }

    /* ------------------------------------------------------------------
       0. Theme Header / Footer & Custom Selector Hider
       ------------------------------------------------------------------ */
    (function applyThemeChromeHider() {
      var rawSelectors = root.getAttribute('data-ig2s-custom-hide') || '';
      var autoHide = root.getAttribute('data-ig2s-hide-chrome') === 'true';
      var hiddenRecords = [];

      function hideElementSafely(el) {
        if (
          !el ||
          el === document.documentElement ||
          el === document.body ||
          el === root ||
          el.contains(root) ||
          root.contains(el)
        ) {
          return;
        }
        if (el.getAttribute('data-ig2s-hidden-chrome') === '1') return;
        hiddenRecords.push({
          el: el,
          prevDisplay: el.style.getPropertyValue('display'),
          prevPriority: el.style.getPropertyPriority('display'),
        });
        el.setAttribute('data-ig2s-hidden-chrome', '1');
        el.style.setProperty('display', 'none', 'important');
      }

      if (autoHide) {
        var defaultCandidates = [
          '.shopify-section-group-header-group',
          '.shopify-section-group-footer-group',
          'div[class*="shopify-section-group-header"]',
          'div[class*="shopify-section-group-footer"]',
          '[id^="shopify-section-sections--"][id*="header"]',
          '[id^="shopify-section-sections--"][id*="announcement"]',
          '[id^="shopify-section-sections--"][id*="topbar"]',
          '[id^="shopify-section-sections--"][id*="footer"]',
          '#shopify-section-header',
          '#shopify-section-announcement-bar',
          '#shopify-section-footer',
          'sticky-header',
          'header-drawer',
          '.header-wrapper',
          '.site-header',
          '.site-footer',
          '.announcement-bar',
          '.announcement-bar-section',
          '.section-header',
          '.section-footer',
          'header:not(.ig2s-header)',
          'footer:not(.ig2s-footer)',
        ];
        defaultCandidates.forEach(function (sel) {
          try {
            document.querySelectorAll(sel).forEach(hideElementSafely);
          } catch (_err) {}
        });
      }

      if (rawSelectors.trim()) {
        var tokens = rawSelectors.split(/[\n,]+/);
        tokens.forEach(function (raw) {
          var cleaned = raw
            .replace(/^\s*(class|id)\s*=\s*/i, '')
            .replace(/["'`]/g, '')
            .trim();
          if (!cleaned) return;

          var candidateQueries = [];
          var first = cleaned.charAt(0);
          if (first === '.' || first === '#' || first === '[') {
            candidateQueries.push(cleaned);
          } else if (/\s+/.test(cleaned)) {
            // Supports pasted multi-class strings like: header-wrapper color-scheme-1
            candidateQueries.push('.' + cleaned.split(/\s+/).filter(Boolean).join('.'));
            candidateQueries.push(cleaned);
          } else {
            candidateQueries.push('.' + cleaned);
            candidateQueries.push('#' + cleaned);
            candidateQueries.push(cleaned);
          }

          candidateQueries.forEach(function (q) {
            try {
              document.querySelectorAll(q).forEach(hideElementSafely);
            } catch (_err) {}
          });

          // Direct ID lookup in case ID contains characters like colons/dashes
          var byId = document.getElementById(cleaned.replace(/^#/, ''));
          if (byId) hideElementSafely(byId);
        });
      }

      cleanups.push(function () {
        hiddenRecords.forEach(function (rec) {
          rec.el.removeAttribute('data-ig2s-hidden-chrome');
          if (rec.prevDisplay) {
            rec.el.style.setProperty('display', rec.prevDisplay, rec.prevPriority || '');
          } else {
            rec.el.style.removeProperty('display');
          }
        });
      });
    })();

    /* ------------------------------------------------------------------
       1. Smooth Anchor Links (#project-form, #ig2s-top)
       ------------------------------------------------------------------ */
    var anchors = root.querySelectorAll('a[href^="#"]');
    anchors.forEach(function (anchor) {
      on(anchor, 'click', function (e) {
        var hash = anchor.getAttribute('href');
        if (!hash || hash === '#') return;
        var target =
          root.querySelector(hash) || document.querySelector(hash);
        if (!target) return;

        e.preventDefault();
        if (globalLenis && !reduced) {
          globalLenis.scrollTo(target, {
            offset: hash.indexOf('top') !== -1 ? 0 : -44,
            duration: 1.22,
            easing: function (t) {
              return Math.min(1, 1.001 - Math.pow(2, -10 * t));
            },
          });
        } else {
          target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
        }
      });
    });

    /* ------------------------------------------------------------------
       2. Magnetic Pill Buttons (Fine Pointer Only)
       ------------------------------------------------------------------ */
    if (!reduced && gsap && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      var magnetics = root.querySelectorAll('[data-ig2s-magnetic]');
      magnetics.forEach(function (wrap) {
        var strength = parseFloat(wrap.getAttribute('data-ig2s-magnetic')) || 0.2;
        var xTo = gsap.quickTo(wrap, 'x', { duration: 0.45, ease: 'power3.out' });
        var yTo = gsap.quickTo(wrap, 'y', { duration: 0.45, ease: 'power3.out' });

        on(wrap, 'mousemove', function (e) {
          var rect = wrap.getBoundingClientRect();
          var cx = rect.left + rect.width / 2;
          var cy = rect.top + rect.height / 2;
          xTo((e.clientX - cx) * strength);
          yTo((e.clientY - cy) * strength);
        });

        on(wrap, 'mouseleave', function () {
          xTo(0);
          yTo(0);
        });
      });
    }

    /* ------------------------------------------------------------------
       3. Interactive iOS Lock Screen Stack + Live Order Loop State
       ------------------------------------------------------------------ */
    var showLessBtn = root.querySelector('[data-ig2s-stack-toggle]');
    var notifStack = root.querySelector('[data-ig2s-notif-stack]');
    var heroEntranceRef = {
      tl: null,
      incomingTl: null,
      loopTimer: null,
      entranceDone: false,
      phoneVisible: true,
      pageVisible: !document.hidden,
      nextOrderNum: 1050,
      basketIdx: 0,
      templateWrap: null,
      scheduleNext: function () {},
      clearTimer: function () {},
    };

    if (notifStack && (!gsap || reduced)) {
      notifStack.classList.add('is-css-fallback');
    }

    function setNotifStackCollapsed(collapse) {
      if (!notifStack || !showLessBtn) return;

      // Pause live incoming loop before folding; finish any in-flight tween cleanly
      heroEntranceRef.clearTimer();
      if (heroEntranceRef.tl && heroEntranceRef.tl.progress() < 1) {
        heroEntranceRef.tl.progress(1);
      }
      if (heroEntranceRef.incomingTl) {
        heroEntranceRef.incomingTl.progress(1);
        heroEntranceRef.incomingTl = null;
      }

      notifStack.classList.toggle('is-collapsed', collapse);
      showLessBtn.setAttribute('aria-expanded', collapse ? 'false' : 'true');

      var label = showLessBtn.querySelector('[data-ig2s-toggle-label]');
      var chevron = showLessBtn.querySelector('svg');
      if (label) {
        label.textContent = collapse ? 'Show more' : 'Show less';
      }
      if (chevron) {
        chevron.style.transform = collapse ? 'rotate(-90deg)' : 'rotate(0deg)';
      }

      if (!gsap || reduced) return;

      var wraps = notifStack.querySelectorAll('.ig2s-notif-wrap');
      var pills = notifStack.querySelectorAll('.ig2s-notif-pill');
      var inners = notifStack.querySelectorAll('[data-ig2s-notif-inner]');
      if (wraps.length < 3 || pills.length < 3) return;

      gsap.killTweensOf([wraps, pills, inners]);

      if (collapse) {
        var h1 = pills[1].offsetHeight || 62;
        var h2 = pills[2].offsetHeight || 62;

        gsap.set([pills[1], pills[2]], { transformOrigin: '50% 100%' });

        gsap.to(wraps[0], {
          marginBottom: 0,
          duration: 0.56,
          ease: iosFluidEase,
        });
        gsap.to(wraps[1], {
          height: 9,
          marginBottom: 0,
          duration: 0.56,
          ease: iosFluidEase,
        });
        gsap.to(wraps[2], {
          height: 8,
          marginBottom: 0,
          duration: 0.56,
          ease: iosFluidEase,
        });

        gsap.to(pills[1], {
          y: -(h1 - 9),
          scale: 0.952,
          opacity: 0.86,
          duration: 0.56,
          ease: iosFluidEase,
        });
        gsap.to(pills[2], {
          y: -(h2 - 8),
          scale: 0.904,
          opacity: 0.58,
          duration: 0.56,
          ease: iosFluidEase,
        });

        if (inners[1] && inners[2]) {
          gsap.to([inners[1], inners[2]], {
            opacity: 0,
            duration: 0.18,
            ease: 'power2.out',
          });
        }
      } else {
        var fullH1 = pills[1].offsetHeight || 62;
        var fullH2 = pills[2].offsetHeight || 62;

        gsap.to(wraps[0], {
          marginBottom: 8,
          duration: 0.62,
          ease: iosFluidEase,
        });
        gsap.to(wraps[1], {
          height: fullH1,
          marginBottom: 8,
          duration: 0.62,
          ease: iosFluidEase,
          onComplete: function () {
            gsap.set(wraps[1], { height: 'auto' });
          },
        });
        gsap.to(wraps[2], {
          height: fullH2,
          marginBottom: 0,
          duration: 0.62,
          ease: iosFluidEase,
          onComplete: function () {
            gsap.set(wraps[2], { height: 'auto' });
          },
        });

        gsap.to([pills[1], pills[2]], {
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 0.64,
          stagger: 0.04,
          ease: iosFluidEase,
          onComplete: function () {
            gsap.set([pills[1], pills[2]], { transformOrigin: '50% 0%' });
            heroEntranceRef.scheduleNext(1800);
          },
        });

        if (inners[1] && inners[2]) {
          gsap.to([inners[1], inners[2]], {
            opacity: 1,
            duration: 0.34,
            delay: 0.1,
            ease: 'power2.out',
          });
        }
      }
    }

    if (showLessBtn && notifStack) {
      on(showLessBtn, 'click', function (e) {
        e.stopPropagation();
        var nextCollapsed = !notifStack.classList.contains('is-collapsed');
        setNotifStackCollapsed(nextCollapsed);
      });

      // Tapping the collapsed notification deck fans it open just like real iOS
      on(notifStack, 'click', function () {
        if (notifStack.classList.contains('is-collapsed')) {
          setNotifStackCollapsed(false);
        }
      });
    }

    /* ------------------------------------------------------------------
       4. Section 02 — The Shift Interactive State & Scent Selector
       ------------------------------------------------------------------ */
    var shiftTrack = root.querySelector('[data-ig2s-shift-track]');
    var shiftPanes = root.querySelectorAll('[data-ig2s-pane]');
    var desktopStepCards = root.querySelectorAll('[data-ig2s-step-btn]');
    var mobileTabs = root.querySelectorAll('[data-ig2s-mobile-tab]');
    var mobileCaption = root.querySelector('[data-ig2s-mobile-caption]');
    var auras = [
      root.querySelector('.ig2s-aura-0'),
      root.querySelector('.ig2s-aura-1'),
      root.querySelector('.ig2s-aura-2'),
    ];
    var chipLeftText = root.querySelector('[data-ig2s-chip-left]');
    var chipRightText = root.querySelector('[data-ig2s-chip-right]');

    var stepMeta = [
      {
        caption: 'Customers find your products on Instagram.',
        left: 'Instagram Post',
        right: 'Direct link in bio',
      },
      {
        caption:
          'They browse options and choose without another back-and-forth conversation.',
        left: 'Live options · £24.00',
        right: 'No waiting for a DM',
      },
      {
        caption: 'You receive an organised order through Shopify.',
        left: 'Paid online',
        right: 'Order #1048 logged',
      },
    ];

    var activeStep = 0;
    var manualLockUntil = 0;

    function applyShiftStep(nextIndex) {
      if (nextIndex === activeStep && shiftPanes[0].classList.contains('is-ready-init')) {
        return;
      }
      activeStep = nextIndex;
      if (shiftPanes[0]) shiftPanes[0].classList.add('is-ready-init');

      shiftPanes.forEach(function (pane, i) {
        var isCurrent = i === nextIndex;
        pane.classList.toggle('is-active', isCurrent);
        pane.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      });

      desktopStepCards.forEach(function (card, i) {
        var isCurrent = i === nextIndex;
        card.classList.toggle('is-active', isCurrent);
        card.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      });

      mobileTabs.forEach(function (tab, i) {
        var isCurrent = i === nextIndex;
        tab.classList.toggle('is-active', isCurrent);
        tab.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      });

      auras.forEach(function (aura, i) {
        if (aura) aura.style.opacity = i === nextIndex ? '1' : '0';
      });

      if (mobileCaption && stepMeta[nextIndex]) {
        mobileCaption.textContent = stepMeta[nextIndex].caption;
      }
      if (chipLeftText && stepMeta[nextIndex]) {
        chipLeftText.textContent = stepMeta[nextIndex].left;
      }
      if (chipRightText && stepMeta[nextIndex]) {
        chipRightText.textContent = stepMeta[nextIndex].right;
      }
    }

    function jumpToShiftStep(idx) {
      applyShiftStep(idx);
      manualLockUntil = Date.now() + 950;

      if (reduced || !shiftTrack) return;
      var scrollable = shiftTrack.offsetHeight - window.innerHeight;
      if (scrollable <= 60) return;

      var trackTop = window.scrollY + shiftTrack.getBoundingClientRect().top;
      var ratios = [0.06, 0.5, 0.9];
      var targetY = Math.max(0, trackTop + scrollable * ratios[idx] - 54);

      if (globalLenis) {
        globalLenis.scrollTo(targetY, { duration: 0.9 });
      } else {
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    }

    if (shiftPanes.length) {
      applyShiftStep(0);
    }

    desktopStepCards.forEach(function (btn) {
      on(btn, 'click', function () {
        jumpToShiftStep(parseInt(btn.getAttribute('data-ig2s-step-btn'), 10) || 0);
      });
    });

    mobileTabs.forEach(function (tab) {
      on(tab, 'click', function () {
        jumpToShiftStep(parseInt(tab.getAttribute('data-ig2s-mobile-tab'), 10) || 0);
      });
    });

    var jumpTriggers = root.querySelectorAll('[data-ig2s-jump]');
    jumpTriggers.forEach(function (btn) {
      on(btn, 'click', function () {
        jumpToShiftStep(parseInt(btn.getAttribute('data-ig2s-jump'), 10) || 0);
      });
    });

    // Interactive Scent Pills inside State 2 -> Updates State 3 Variant Text
    var scentBtns = root.querySelectorAll('[data-ig2s-scent]');
    var scentLabelOut = root.querySelector('[data-ig2s-selected-scent]');
    scentBtns.forEach(function (btn) {
      on(btn, 'click', function () {
        var val = btn.getAttribute('data-ig2s-scent') || 'Vanilla';
        scentBtns.forEach(function (b) {
          b.classList.toggle('is-selected', b === btn);
        });
        if (scentLabelOut) {
          scentLabelOut.textContent = 'Scent: ' + val;
        }
      });
    });

    /* ------------------------------------------------------------------
       5. Section 03 — The Package Accessible Hover + Click Accordion
       ------------------------------------------------------------------ */
    var packageRows = root.querySelectorAll('[data-ig2s-pkg-row]');
    var finePointer =
      window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function setOpenPkgRow(targetRow, allowClose) {
      packageRows.forEach(function (row) {
        var btn = row.querySelector('.ig2s-package-trigger');
        var isMatch = row === targetRow;
        if (isMatch && allowClose && row.classList.contains('is-open')) {
          row.classList.remove('is-open');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        } else {
          row.classList.toggle('is-open', isMatch);
          if (btn) btn.setAttribute('aria-expanded', isMatch ? 'true' : 'false');
        }
      });
    }

    packageRows.forEach(function (row) {
      var trigger = row.querySelector('.ig2s-package-trigger');
      on(trigger, 'click', function () {
        setOpenPkgRow(row, true);
      });
      if (finePointer) {
        on(row, 'mouseenter', function () {
          setOpenPkgRow(row, false);
        });
      }
    });

    /* ------------------------------------------------------------------
       5B. Section 06 — Verified Partner Reviews Mobile Expand Toggle
       ------------------------------------------------------------------ */
    var reviewsMoreBtn = root.querySelector('[data-ig2s-reviews-more-btn]');
    var reviewsSecondary = root.querySelector('[data-ig2s-reviews-secondary]');
    if (reviewsMoreBtn && reviewsSecondary) {
      var reviewsMoreLabel = reviewsMoreBtn.querySelector(
        '[data-ig2s-reviews-more-label]'
      );
      var reviewsChevron = reviewsMoreBtn.querySelector(
        '[data-ig2s-reviews-chevron]'
      );
      on(reviewsMoreBtn, 'click', function () {
        var expanded = reviewsSecondary.classList.toggle('is-expanded');
        reviewsMoreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (reviewsMoreLabel) {
          reviewsMoreLabel.textContent = expanded
            ? 'Show fewer reviews'
            : 'Read 3 more verified reviews';
        }
        if (reviewsChevron) {
          reviewsChevron.style.transform = expanded
            ? 'rotate(180deg)'
            : 'rotate(0deg)';
        }
        if (ScrollTrigger) {
          window.requestAnimationFrame(function () {
            ScrollTrigger.refresh();
          });
        }
      });
    }

    /* ------------------------------------------------------------------
       6. Section 07 — Shopify Contact Form Validation & Radio Pills
       ------------------------------------------------------------------ */
    var countRadios = root.querySelectorAll('.ig2s-count-input');
    var countLabels = root.querySelectorAll('.ig2s-count-label');
    countRadios.forEach(function (radio) {
      on(radio, 'change', function () {
        countLabels.forEach(function (lbl) {
          var input = lbl.querySelector('.ig2s-count-input');
          lbl.classList.toggle('is-selected', Boolean(input && input.checked));
        });
      });
    });

    var form = root.querySelector('.ig2s-contact-form');
    if (form) {
      var clearFieldError = function (fieldWrap) {
        if (!fieldWrap) return;
        var err = fieldWrap.querySelector('[data-ig2s-error]');
        var inp = fieldWrap.querySelector('input, textarea');
        if (err) err.hidden = true;
        if (inp) inp.setAttribute('aria-invalid', 'false');
      };

      form.querySelectorAll('input, textarea').forEach(function (inp) {
        on(inp, 'input', function () {
          clearFieldError(inp.closest('[data-ig2s-field]'));
        });
        on(inp, 'change', function () {
          clearFieldError(inp.closest('[data-ig2s-field]'));
        });
      });

      on(form, 'submit', function (e) {
        var valid = true;

        var checkText = function (selector, msg) {
          var input = form.querySelector(selector);
          if (!input) return;
          var wrap = input.closest('[data-ig2s-field]');
          var err = wrap ? wrap.querySelector('[data-ig2s-error]') : null;
          var val = input.value.trim();
          var fieldOk = val.length > 0;
          if (fieldOk && input.type === 'email') {
            fieldOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
          }
          if (!fieldOk) {
            valid = false;
            input.setAttribute('aria-invalid', 'true');
            if (err) {
              err.textContent = msg;
              err.hidden = false;
            }
          }
        };

        checkText('[name="contact[name]"]', 'Please enter your name.');
        checkText(
          '[name="contact[email]"]',
          'Please enter a valid email address.'
        );
        checkText(
          '[name="contact[Instagram_Account]"]',
          'Please share your Instagram handle or profile URL.'
        );
        checkText(
          '[name="contact[Products_Sold]"]',
          'Please briefly tell me what you sell.'
        );

        var countChecked = form.querySelector(
          '[name="contact[Product_Count]"]:checked'
        );
        var countWrap = form.querySelector('[data-ig2s-count-field]');
        if (countWrap) {
          var countErr = countWrap.querySelector('[data-ig2s-error]');
          if (!countChecked) {
            valid = false;
            if (countErr) countErr.hidden = false;
          } else if (countErr) {
            countErr.hidden = true;
          }
        }

        var consentBox = form.querySelector('[name="contact[Consent]"]');
        var consentWrap = form.querySelector('[data-ig2s-consent-field]');
        if (consentBox && consentWrap) {
          var consentErr = consentWrap.querySelector('[data-ig2s-error]');
          if (!consentBox.checked) {
            valid = false;
            consentBox.setAttribute('aria-invalid', 'true');
            if (consentErr) consentErr.hidden = false;
          } else if (consentErr) {
            consentErr.hidden = true;
          }
        }

        if (!valid) {
          e.preventDefault();
          return;
        }

        // Demo preview fallback when running outside live Shopify backend
        if (form.getAttribute('data-ig2s-preview-mode') === 'true') {
          e.preventDefault();
          var surface = root.querySelector('[data-ig2s-form-surface]');
          var successTpl = root.querySelector('[data-ig2s-preview-success]');
          if (surface && successTpl) {
            form.hidden = true;
            successTpl.hidden = false;
          }
        }
      });
    }

    // Auto-scroll to #project-form if Shopify just posted the form
    if (
      window.location.search.indexOf('contact_posted=true') !== -1 ||
      window.location.hash === '#GrowthlyProjectForm'
    ) {
      var formSection = root.querySelector('#project-form');
      if (formSection) {
        setTimeout(function () {
          formSection.scrollIntoView({ behavior: 'auto' });
        }, 60);
      }
    }

    /* ------------------------------------------------------------------
       7. GSAP + ScrollTrigger $1M Awwwards Choreography
       ------------------------------------------------------------------ */
    if (reduced || !gsap || !ScrollTrigger) {
      // Ensure scrubbed headings are fully opaque if reduced-motion or no GSAP
      root.querySelectorAll('[data-ig2s-scrub]').forEach(function (el) {
        el.style.opacity = '1';
      });
      instances.set(root, { cleanups: cleanups, triggers: [] });
      return;
    }

    var ctx = gsap.context(function () {
      /* A. Header Optical Scroll Progress Filament */
      var filament = root.querySelector('.ig2s-progress-filament');
      if (filament) {
        gsap.to(filament, {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: 'bottom bottom',
            scrub: 0.25,
          },
        });
      }

      /* B. Hero Finite Entrance + Authentic iOS Lock Screen Order Arrival */
      var maskInners = root.querySelectorAll('.ig2s-mask-inner');
      var heroEyebrow = root.querySelector('.ig2s-hero-eyebrow');
      var heroSubWrap = root.querySelector('[data-ig2s-hero-sub]');
      var heroPhoneWin = root.querySelector('.ig2s-hero-iphone-window');
      var screenWake = root.querySelector('[data-ig2s-screen-wake]');
      var notifGroup = root.querySelector('.ig2s-notif-group');
      var notifHeader = root.querySelector('.ig2s-notif-header');
      var notifControls = root.querySelector('.ig2s-notif-controls');
      var notifWraps = root.querySelectorAll('.ig2s-notif-wrap');
      var notifPills = root.querySelectorAll('.ig2s-notif-pill');
      var notifSheens = root.querySelectorAll('[data-ig2s-notif-sheen]');
      var focusPill = root.querySelector('.ig2s-focus-pill');

      var heroTl = gsap.timeline({ defaults: { ease: 'expo.out' } });
      heroEntranceRef.tl = heroTl;

      if (heroEyebrow) {
        heroTl.fromTo(
          heroEyebrow,
          { opacity: 0, y: 14 },
          { opacity: 1, y: 0, duration: 0.72 },
          0.02
        );
      }
      if (maskInners.length) {
        heroTl.fromTo(
          maskInners,
          { yPercent: 108, opacity: 0 },
          { yPercent: 0, opacity: 1, duration: 0.92, stagger: 0.09 },
          0.06
        );
      }
      if (heroSubWrap) {
        heroTl.fromTo(
          heroSubWrap,
          { opacity: 0, y: 22 },
          { opacity: 1, y: 0, duration: 0.84 },
          0.22
        );
      }
      if (heroPhoneWin) {
        heroTl.fromTo(
          heroPhoneWin,
          { opacity: 0, y: 36, scale: 0.97 },
          { opacity: 1, y: 0, scale: 1, duration: 1.05 },
          0.24
        );
      }

      if (notifWraps.length === 3 && notifPills.length === 3) {
        // Capture pristine notification card template BEFORE GSAP applies inline styles
        heroEntranceRef.templateWrap = notifWraps[0].cloneNode(true);

        var LIVE_BASKETS = [
          '£54.00 · Paid, 2 items from Online Store',
          '£38.00 · Paid, 1 item from Online Store',
          '£68.00 · Paid, 2 items from Online Store',
          '£29.00 · Paid, 1 item from Online Store',
          '£42.00 · Paid, 1 item from Online Store',
          '£26.00 · Paid, 1 item from Online Store',
        ];

        heroEntranceRef.clearTimer = function () {
          if (heroEntranceRef.loopTimer) {
            window.clearTimeout(heroEntranceRef.loopTimer);
            heroEntranceRef.loopTimer = null;
          }
        };

        var pushLiveIncomingOrder = function () {
          heroEntranceRef.loopTimer = null;
          if (
            !notifStack ||
            !heroEntranceRef.templateWrap ||
            !heroEntranceRef.entranceDone ||
            !heroEntranceRef.phoneVisible ||
            !heroEntranceRef.pageVisible ||
            notifStack.classList.contains('is-collapsed')
          ) {
            return;
          }

          if (heroEntranceRef.incomingTl) {
            heroEntranceRef.incomingTl.progress(1);
            heroEntranceRef.incomingTl = null;
          }

          var existingWraps = notifStack.querySelectorAll('.ig2s-notif-wrap');
          if (existingWraps.length < 3) return;

          var old0 = existingWraps[0];
          var old1 = existingWraps[1];
          var old2 = existingWraps[2];
          var old0Pill = old0.querySelector('.ig2s-notif-pill');
          var old1Pill = old1.querySelector('.ig2s-notif-pill');
          var old2Pill = old2.querySelector('.ig2s-notif-pill');

          var cardH = (old0Pill && old0Pill.offsetHeight) || 62;
          var old2H = (old2Pill && old2Pill.offsetHeight) || cardH;

          // Build pristine incoming notification at the top of the stack
          var newWrap = heroEntranceRef.templateWrap.cloneNode(true);
          var orderNum = heroEntranceRef.nextOrderNum++;
          var basketLine =
            LIVE_BASKETS[heroEntranceRef.basketIdx++ % LIVE_BASKETS.length];

          newWrap.setAttribute('data-ig2s-notif', String(orderNum));
          var titleEl = newWrap.querySelector('.ig2s-notif-title');
          var timeEl = newWrap.querySelector('.ig2s-notif-time');
          var bodyEl = newWrap.querySelector('.ig2s-notif-body');
          if (titleEl) titleEl.textContent = 'New order #' + orderNum;
          if (timeEl) timeEl.textContent = 'now';
          if (bodyEl) bodyEl.textContent = basketLine;

          // Subtly age existing visible timestamps as newer order arrives
          var old0Time = old0.querySelector('.ig2s-notif-time');
          var old1Time = old1.querySelector('.ig2s-notif-time');
          if (old0Time) old0Time.textContent = 'just now';
          if (old1Time) old1Time.textContent = '1m ago';

          var newPill = newWrap.querySelector('.ig2s-notif-pill');
          var newIcon =
            newWrap.querySelector('[data-ig2s-app-icon]') ||
            newWrap.querySelector('svg');
          var newSheen = newWrap.querySelector('[data-ig2s-notif-sheen]');

          gsap.set(newWrap, { height: 0, marginBottom: 0 });
          if (newPill) {
            gsap.set(newPill, {
              opacity: 0,
              y: -18,
              scale: 0.925,
              transformOrigin: '50% 0%',
            });
          }
          gsap.set(old2, { height: old2H, marginBottom: 0 });

          notifStack.insertBefore(newWrap, notifStack.firstChild);

          var inTl = gsap.timeline({
            onComplete: function () {
              if (old2 && old2.parentNode) {
                old2.parentNode.removeChild(old2);
              }
              gsap.set([newWrap, old1], { clearProps: 'height,marginBottom' });
              heroEntranceRef.incomingTl = null;
              heroEntranceRef.scheduleNext(2850);
            },
          });
          heroEntranceRef.incomingTl = inTl;

          // Soft ambient Lock Screen wake pulse
          if (screenWake) {
            inTl
              .to(
                screenWake,
                { opacity: 0.72, duration: 0.34, ease: 'power2.out' },
                0
              )
              .to(
                screenWake,
                { opacity: 0.24, duration: 0.62, ease: 'power2.inOut' },
                0.34
              );
          }

          // Algebraically constant stack height handoff: +cardH+8 at top, -8-old2H at bottom
          inTl.to(
            newWrap,
            {
              height: cardH,
              marginBottom: 8,
              duration: 0.82,
              ease: iosFluidEase,
            },
            0.02
          );
          inTl.to(
            old1,
            {
              marginBottom: 0,
              duration: 0.82,
              ease: iosFluidEase,
            },
            0.02
          );
          inTl.to(
            old2,
            {
              height: 0,
              marginBottom: 0,
              duration: 0.82,
              ease: iosFluidEase,
            },
            0.02
          );

          if (old2Pill) {
            inTl.to(
              old2Pill,
              {
                opacity: 0,
                scale: 0.93,
                y: 6,
                duration: 0.52,
                ease: 'power2.out',
              },
              0.02
            );
          }

          if (newPill) {
            inTl.to(
              newPill,
              { opacity: 1, duration: 0.34, ease: 'power2.out' },
              0.04
            );
            inTl.to(
              newPill,
              { y: 0, scale: 1, duration: 0.84, ease: iosFluidEase },
              0.04
            );
          }

          if (newIcon) {
            inTl.fromTo(
              newIcon,
              { scale: 0.86 },
              { scale: 1, duration: 0.68, ease: iosFluidEase },
              0.06
            );
          }

          if (newSheen) {
            inTl
              .fromTo(
                newSheen,
                { xPercent: -120, opacity: 0 },
                { xPercent: 210, opacity: 0.68, duration: 0.86, ease: 'power2.inOut' },
                0.08
              )
              .to(newSheen, { opacity: 0, duration: 0.24 }, 0.72);
          }

          if (old0Pill && old1Pill) {
            inTl
              .to(
                [old0Pill, old1Pill],
                { scale: 0.988, duration: 0.2, ease: 'power2.out' },
                0.06
              )
              .to(
                [old0Pill, old1Pill],
                { scale: 1, duration: 0.56, stagger: 0.04, ease: iosFluidEase },
                0.26
              );
          }
        };

        heroEntranceRef.scheduleNext = function (delayMs) {
          heroEntranceRef.clearTimer();
          if (
            !heroEntranceRef.entranceDone ||
            !heroEntranceRef.phoneVisible ||
            !heroEntranceRef.pageVisible ||
            !notifStack ||
            notifStack.classList.contains('is-collapsed')
          ) {
            return;
          }
          heroEntranceRef.loopTimer = window.setTimeout(
            pushLiveIncomingOrder,
            typeof delayMs === 'number' ? delayMs : 2850
          );
        };

        // Pause automatically when iPhone scrolls out of viewport; resume as soon as visible
        if (heroPhoneWin && 'IntersectionObserver' in window) {
          var phoneObserver = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                heroEntranceRef.phoneVisible = entry.isIntersecting;
                if (entry.isIntersecting) {
                  if (!heroEntranceRef.incomingTl && !heroEntranceRef.loopTimer) {
                    heroEntranceRef.scheduleNext(1150);
                  }
                } else {
                  heroEntranceRef.clearTimer();
                }
              });
            },
            { threshold: 0.12 }
          );
          phoneObserver.observe(heroPhoneWin);
          cleanups.push(function () {
            phoneObserver.disconnect();
          });
        }

        var onVisChange = function () {
          heroEntranceRef.pageVisible = !document.hidden;
          if (heroEntranceRef.pageVisible) {
            if (!heroEntranceRef.incomingTl && !heroEntranceRef.loopTimer) {
              heroEntranceRef.scheduleNext(1200);
            }
          } else {
            heroEntranceRef.clearTimer();
          }
        };
        on(document, 'visibilitychange', onVisChange);

        cleanups.push(function () {
          heroEntranceRef.clearTimer();
          if (heroEntranceRef.incomingTl) {
            heroEntranceRef.incomingTl.kill();
            heroEntranceRef.incomingTl = null;
          }
        });

        // Measure natural heights before collapsing live-arrival slots (#1049 & #1048)
        var h0 = notifPills[0].offsetHeight || 62;
        var h1 = notifPills[1].offsetHeight || 62;
        var icon0 =
          notifPills[0].querySelector('[data-ig2s-app-icon]') ||
          notifPills[0].querySelector('svg');
        var icon1 =
          notifPills[1].querySelector('[data-ig2s-app-icon]') ||
          notifPills[1].querySelector('svg');

        // Pre-arrival state: Order #1047 (18m ago) is already resting calmly on Lock Screen
        gsap.set([notifWraps[0], notifWraps[1]], {
          height: 0,
          marginBottom: 0,
        });
        gsap.set([notifPills[0], notifPills[1]], {
          opacity: 0,
          y: -18,
          scale: 0.925,
          transformOrigin: '50% 0%',
        });
        if (notifControls) {
          gsap.set(notifControls, { opacity: 0, scale: 0.92 });
        }
        if (focusPill) {
          gsap.set(focusPill, { opacity: 0, y: 6, scale: 0.96 });
        }
        if (notifHeader) {
          heroTl.fromTo(
            notifHeader,
            { opacity: 0, y: 4 },
            { opacity: 1, y: 0, duration: 0.55 },
            0.52
          );
        }
        heroTl.fromTo(
          notifPills[2],
          { opacity: 0.45, y: 6, scale: 0.98 },
          { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: iosFluidEase },
          0.54
        );

        /* --- Live Arrival 1 (t = 0.86s): New order #1048 (£42.00 · Paid) --- */
        if (screenWake) {
          heroTl
            .to(
              screenWake,
              { opacity: 0.68, duration: 0.36, ease: 'power2.out' },
              0.82
            )
            .to(
              screenWake,
              { opacity: 0.22, duration: 0.52, ease: 'power2.inOut' },
              1.18
            );
        }

        // Smoothly open top slot & push #1047 downward like real iOS SpringBoard
        heroTl.to(
          notifWraps[1],
          {
            height: h1,
            marginBottom: 8,
            duration: 0.78,
            ease: iosFluidEase,
          },
          0.86
        );
        heroTl.to(
          notifPills[1],
          {
            opacity: 1,
            duration: 0.34,
            ease: 'power2.out',
          },
          0.88
        );
        heroTl.to(
          notifPills[1],
          {
            y: 0,
            scale: 1,
            duration: 0.82,
            ease: iosFluidEase,
          },
          0.88
        );
        if (icon1) {
          heroTl.fromTo(
            icon1,
            { scale: 0.86 },
            { scale: 1, duration: 0.68, ease: iosFluidEase },
            0.9
          );
        }
        if (notifSheens[1]) {
          heroTl
            .fromTo(
              notifSheens[1],
              { xPercent: -120, opacity: 0 },
              { xPercent: 210, opacity: 0.65, duration: 0.85, ease: 'power2.inOut' },
              0.92
            )
            .to(notifSheens[1], { opacity: 0, duration: 0.25 }, 1.55);
        }
        // Sympathetic cushion on #1047 as #1048 pushes it down
        heroTl
          .to(
            notifPills[2],
            { scale: 0.986, duration: 0.2, ease: 'power2.out' },
            0.9
          )
          .to(
            notifPills[2],
            { scale: 1, duration: 0.56, ease: iosFluidEase },
            1.1
          );
        // Group controls ("Show less" + clear) softly appear once stack has 2+ orders
        if (notifControls) {
          heroTl.to(
            notifControls,
            { opacity: 1, scale: 1, duration: 0.52, ease: iosFluidEase },
            1.04
          );
        }

        /* --- Live Arrival 2 (t = 1.62s, +760ms): New order #1049 (£26.00 · Paid) --- */
        if (screenWake) {
          heroTl
            .to(
              screenWake,
              { opacity: 0.8, duration: 0.36, ease: 'power2.out' },
              1.58
            )
            .to(
              screenWake,
              { opacity: 0.28, duration: 0.65, ease: 'power2.inOut' },
              1.94
            );
        }

        // Open top slot & glide BOTH #1048 and #1047 downward in unison
        heroTl.to(
          notifWraps[0],
          {
            height: h0,
            marginBottom: 8,
            duration: 0.8,
            ease: iosFluidEase,
          },
          1.62
        );
        heroTl.to(
          notifPills[0],
          {
            opacity: 1,
            duration: 0.34,
            ease: 'power2.out',
          },
          1.64
        );
        heroTl.to(
          notifPills[0],
          {
            y: 0,
            scale: 1,
            duration: 0.84,
            ease: iosFluidEase,
          },
          1.64
        );
        if (icon0) {
          heroTl.fromTo(
            icon0,
            { scale: 0.86 },
            { scale: 1, duration: 0.68, ease: iosFluidEase },
            1.66
          );
        }
        if (notifSheens[0]) {
          heroTl
            .fromTo(
              notifSheens[0],
              { xPercent: -120, opacity: 0 },
              { xPercent: 210, opacity: 0.7, duration: 0.88, ease: 'power2.inOut' },
              1.68
            )
            .to(notifSheens[0], { opacity: 0, duration: 0.25 }, 2.32);
        }
        // Sympathetic cushion on #1048 and #1047 as #1049 settles above them
        heroTl
          .to(
            [notifPills[1], notifPills[2]],
            { scale: 0.988, duration: 0.2, ease: 'power2.out' },
            1.66
          )
          .to(
            [notifPills[1], notifPills[2]],
            { scale: 1, duration: 0.58, stagger: 0.04, ease: iosFluidEase },
            1.86
          );

        // Status badge ("Store ready · You’re offline") settles last, then stop
        if (focusPill) {
          heroTl.to(
            focusPill,
            { opacity: 1, y: 0, scale: 1, duration: 0.62, ease: iosFluidEase },
            2.16
          );
        }

        heroTl.eventCallback('onComplete', function () {
          if (notifStack && !notifStack.classList.contains('is-collapsed')) {
            gsap.set([notifWraps[0], notifWraps[1]], { height: 'auto' });
          }
          heroEntranceRef.entranceDone = true;
          heroEntranceRef.scheduleNext(2600);
        });
      }

      /* C. Hero Scroll-Linked Apple Keynote Depth Dolly */
      var heroSection = root.querySelector('.ig2s-hero');
      var heroCopyStack = root.querySelector('.ig2s-hero-stack');
      var citrusHalo = root.querySelector('.ig2s-citrus-halo');

      if (heroSection && heroCopyStack) {
        gsap.to(heroCopyStack, {
          y: -52,
          scale: 0.94,
          opacity: 0.12,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: '75% top',
            scrub: 0.4,
          },
        });
      }
      if (heroSection && heroPhoneWin) {
        gsap.to(heroPhoneWin, {
          y: -28,
          scale: 1.025,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.45,
          },
        });
      }
      if (heroSection && citrusHalo) {
        gsap.to(citrusHalo, {
          scale: 1.24,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: 'bottom top',
            scrub: 0.5,
          },
        });
      }
      if (heroSection && notifGroup) {
        gsap.to(notifGroup, {
          y: -10,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: '80% top',
            scrub: 0.4,
          },
        });
      }

      /* D. Word-by-Word Scroll Scrubbed Headings */
      root.querySelectorAll('[data-ig2s-scrub]').forEach(function (heading) {
        var rawText = heading.textContent.trim();
        if (!rawText) return;
        heading.setAttribute('aria-label', rawText);
        var words = rawText.split(/\s+/);
        heading.innerHTML = words
          .map(function (w) {
            return '<span class="ig2s-scrub-word" aria-hidden="true">' + w + '</span>';
          })
          .join(' ');

        var wordEls = heading.querySelectorAll('.ig2s-scrub-word');
        gsap.to(wordEls, {
          opacity: 1,
          y: 0,
          stagger: 0.08,
          ease: 'none',
          scrollTrigger: {
            trigger: heading,
            start: 'top 88%',
            end: 'top 38%',
            scrub: 0.35,
          },
        });
      });

      /* E. Section 01 — Live Scroll-Driven DM Bubble Stream */
      var dmCard = root.querySelector('.ig2s-dm-card');
      var dmRows = root.querySelectorAll('.ig2s-dm-row');
      if (dmCard && dmRows.length) {
        gsap.fromTo(
          dmCard,
          { y: 32 },
          {
            y: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: dmCard,
              start: 'top 88%',
              end: 'center 48%',
              scrub: 0.4,
            },
          }
        );

        dmRows.forEach(function (row, i) {
          var isCustomer = row.classList.contains('ig2s-dm-customer');
          gsap.fromTo(
            row,
            {
              opacity: 0.14,
              scale: 0.88,
              y: 18,
              x: isCustomer ? -10 : 10,
            },
            {
              opacity: 1,
              scale: 1,
              y: 0,
              x: 0,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: dmCard,
                start: 'top+=' + i * 36 + ' 86%',
                end: 'top+=' + (i * 36 + 130) + ' 58%',
                scrub: 0.35,
              },
            }
          );
        });
      }

      var problemItems = root.querySelectorAll('.ig2s-problem-item');
      if (problemItems.length) {
        gsap.fromTo(
          problemItems,
          { opacity: 0, y: 20, x: 12 },
          {
            opacity: 1,
            y: 0,
            x: 0,
            duration: 0.7,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: problemItems[0],
              start: 'top 86%',
              once: true,
            },
          }
        );
      }

      /* F. Section 02 — Pinned 3-Act Cinema Stage */
      if (shiftTrack) {
        var progressFills = root.querySelectorAll('.ig2s-step-progress-fill');
        var halfPhone = root.querySelector('.ig2s-half-iphone');
        var chipLeft = root.querySelector('.ig2s-chip-left');
        var chipRight = root.querySelector('.ig2s-chip-right');

        ScrollTrigger.create({
          trigger: shiftTrack,
          start: 'top 12%',
          end: 'bottom 92%',
          scrub: 0.3,
          onUpdate: function (self) {
            var p = self.progress;

            // Update continuous liquid progress bars on the 3 step cards
            progressFills.forEach(function (fill, idx) {
              var segStart = idx / 3;
              var segEnd = (idx + 1) / 3;
              var local = Math.max(
                0,
                Math.min(1, (p - segStart) / (segEnd - segStart))
              );
              fill.style.transform = 'scaleX(' + local.toFixed(4) + ')';
            });

            if (halfPhone) {
              var rise = Math.max(0, (1 - Math.min(1, p * 2.2)) * 18);
              halfPhone.style.transform = 'translateY(' + rise.toFixed(2) + 'px)';
            }
            if (chipLeft) {
              chipLeft.style.transform =
                'translateY(' + (16 - p * 32).toFixed(2) + 'px)';
            }
            if (chipRight) {
              chipRight.style.transform =
                'translateY(' + (-12 + p * 26).toFixed(2) + 'px)';
            }

            if (Date.now() < manualLockUntil) return;
            var next = p >= 0.66 ? 2 : p >= 0.33 ? 1 : 0;
            applyShiftStep(next);
          },
        });
      }

      /* G. Section 03 — Curved Monolith Portal Expansion & Self-Drawing Hairlines */
      var pkgOuter = root.querySelector('.ig2s-package-outer');
      var pkgInner = root.querySelector('.ig2s-package');
      if (pkgOuter && pkgInner) {
        gsap.fromTo(
          pkgInner,
          { scale: 0.935, borderRadius: '44px' },
          {
            scale: 1,
            borderRadius: '0px',
            ease: 'none',
            scrollTrigger: {
              trigger: pkgOuter,
              start: 'top 95%',
              end: 'top 24%',
              scrub: 0.4,
            },
          }
        );
      }

      var hairlines = root.querySelectorAll('.ig2s-package-hairline');
      if (hairlines.length) {
        gsap.fromTo(
          hairlines,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.85,
            stagger: 0.06,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: hairlines[0],
              start: 'top 88%',
              once: true,
            },
          }
        );
      }

      /* H. Section 04 — Live Scroll-Drawn Lime Beam & Differential Parallax Cards */
      var processSec = root.querySelector('.ig2s-process');
      var beamFill = root.querySelector('.ig2s-process-beam-fill');
      var processCards = root.querySelectorAll('.ig2s-process-step');
      if (processSec && beamFill) {
        gsap.fromTo(
          beamFill,
          { scaleX: 0 },
          {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: processSec,
              start: 'top 78%',
              end: 'bottom 58%',
              scrub: 0.4,
            },
          }
        );
      }
      if (processSec && processCards.length) {
        [34, 60, 88].forEach(function (startY, idx) {
          if (!processCards[idx]) return;
          gsap.fromTo(
            processCards[idx],
            { y: startY, opacity: 0.35 },
            {
              y: 0,
              opacity: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: processSec,
                start: 'top 82%',
                end: 'center 52%',
                scrub: 0.45,
              },
            }
          );
        });
      }

      /* I. Section 06 — Verified Partner Reviews Scorecard & Cards */
      var scorecardEl = root.querySelector('[data-ig2s-scorecard]');
      var distFillEl = root.querySelector('[data-ig2s-dist-fill]');
      var metricFillEls = root.querySelectorAll('[data-ig2s-metric-fill]');
      var reviewCards = root.querySelectorAll('[data-ig2s-review-card]');
      var compactReviewCards = root.querySelectorAll('[data-ig2s-review-compact]');

      if (scorecardEl) {
        gsap.fromTo(
          scorecardEl,
          { opacity: 0, y: 26 },
          {
            opacity: 1,
            y: 0,
            duration: 0.78,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: scorecardEl,
              start: 'top 86%',
              once: true,
            },
          }
        );
      }

      if (distFillEl) {
        gsap.fromTo(
          distFillEl,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.92,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: scorecardEl || distFillEl,
              start: 'top 84%',
              once: true,
            },
          }
        );
      }

      if (metricFillEls.length) {
        gsap.fromTo(
          metricFillEls,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.88,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: scorecardEl || metricFillEls[0],
              start: 'top 84%',
              once: true,
            },
          }
        );
      }

      if (reviewCards.length) {
        gsap.fromTo(
          reviewCards,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.76,
            stagger: 0.1,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: reviewCards[0],
              start: 'top 86%',
              once: true,
            },
          }
        );
      }

      if (compactReviewCards.length) {
        gsap.fromTo(
          compactReviewCards,
          { opacity: 0, y: 18 },
          {
            opacity: 1,
            y: 0,
            duration: 0.68,
            stagger: 0.08,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: compactReviewCards[0],
              start: 'top 90%',
              once: true,
            },
          }
        );
      }

      /* J. Section 07 — Dark Theater Finale Portal Expansion */
      var enqOuter = root.querySelector('.ig2s-enquiry-outer');
      var enqInner = root.querySelector('.ig2s-enquiry');
      var formSurface = root.querySelector('.ig2s-form-surface');
      if (enqOuter && enqInner) {
        gsap.fromTo(
          enqInner,
          { scale: 0.94, borderRadius: '44px' },
          {
            scale: 1,
            borderRadius: '0px',
            ease: 'none',
            scrollTrigger: {
              trigger: enqOuter,
              start: 'top 95%',
              end: 'top 28%',
              scrub: 0.4,
            },
          }
        );
      }
      if (enqOuter && formSurface) {
        gsap.fromTo(
          formSurface,
          { y: 36 },
          {
            y: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: enqOuter,
              start: 'top 85%',
              end: 'top 28%',
              scrub: 0.45,
            },
          }
        );
      }
    }, root);

    instances.set(root, { cleanups: cleanups, ctx: ctx });
  }

  function destroySection(root) {
    var inst = instances.get(root);
    if (!inst) return;
    if (inst.ctx) inst.ctx.revert();
    inst.cleanups.forEach(function (fn) {
      fn();
    });
    instances.delete(root);
  }

  function bootAll() {
    document.querySelectorAll('[data-ig2s-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Shopify Theme Editor Lifecycle Events
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-ig2s-section]') || e.target;
    if (root && root.hasAttribute('data-ig2s-section')) {
      initSection(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-ig2s-section]') || e.target;
    if (root) destroySection(root);
  });
})();
